import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, AtSign, AlertTriangle, Languages, Undo2 } from "lucide-react";

function extraerEmail(remitente) {
  const m = (remitente || "").match(/[^<\s]+@[^>\s]+/);
  return m ? m[0] : "";
}

export default function ResponderAccionDialog({ open, onOpenChange, accion, marcas, onSent }) {
  const [form, setForm] = useState({ destinatario: "", asunto: "", cuerpo: "", marca_slug: "", idioma: "es" });
  const [sending, setSending] = useState(false);
  const [traduciendo, setTraduciendo] = useState(false);
  const [cuerpoAntesTraducir, setCuerpoAntesTraducir] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open && accion) {
      const asuntoOriginal = accion.asunto || "";
      setForm({
        destinatario: extraerEmail(accion.remitente),
        asunto: asuntoOriginal.trim().toLowerCase().startsWith("re:") ? asuntoOriginal : `Re: ${asuntoOriginal}`.trim(),
        cuerpo: "",
        marca_slug: accion.marca || "",
        idioma: accion.parsed?.idioma_cliente || "es",
      });
      setCuerpoAntesTraducir(null);
    }
  }, [open, accion]);

  const marcaSel = (marcas || []).find((m) => m.slug === form.marca_slug);
  const conectada = marcaSel ? !!marcaSel.envio_conectado : false;
  const idiomaClienteObjetivo = accion?.parsed?.idioma_cliente && accion.parsed.idioma_cliente !== "es"
    ? accion.parsed.idioma_cliente : "en";

  const traducirAlCliente = async () => {
    if (!form.cuerpo.trim()) return toast.error("Escribí primero el mensaje en español.");
    setTraduciendo(true);
    try {
      const { data } = await api.post("/captura/traducir-texto", {
        texto: form.cuerpo, idioma_destino: idiomaClienteObjetivo,
      });
      setCuerpoAntesTraducir(form.cuerpo);
      set({ cuerpo: data.texto_traducido });
      toast.success(`Traducido. Revisá el texto antes de enviarlo.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTraduciendo(false);
    }
  };

  const deshacerTraduccion = () => {
    if (cuerpoAntesTraducir === null) return;
    set({ cuerpo: cuerpoAntesTraducir });
    setCuerpoAntesTraducir(null);
  };

  const enviar = async () => {
    if (!form.cuerpo.trim()) return toast.error("Escribí el mensaje para el cliente.");
    if (!form.destinatario.trim()) return toast.error("Indicá el destinatario.");
    setSending(true);
    try {
      await api.post(`/captura/acciones/${accion.id}/responder`, {
        cuerpo: form.cuerpo,
        asunto: form.asunto,
        destinatario: form.destinatario,
        marca_slug: form.marca_slug || undefined,
        idioma: form.idioma,
      });
      toast.success("Respuesta enviada. El correo salió de la lista de Acciones.");
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!accion) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="responder-accion-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Send className="h-5 w-5" /> Responder al cliente
          </DialogTitle>
          <DialogDescription>El correo sale directo desde la app, desde la casilla de la marca.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <p
              className="flex-1 text-[12px] inline-flex items-center gap-1.5 px-2 py-1.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium"
              data-testid="responder-sale-de"
            >
              <AtSign className="h-3.5 w-3.5" />
              Sale de: {marcaSel ? `${marcaSel.remitente_nombre || marcaSel.nombre} <${marcaSel.casilla_respuesta}>` : "—"}
            </p>
            <Select value={form.marca_slug} onValueChange={(v) => set({ marca_slug: v })}>
              <SelectTrigger className="w-40" data-testid="responder-marca-select"><SelectValue placeholder="Marca" /></SelectTrigger>
              <SelectContent>
                {(marcas || []).map((m) => (
                  <SelectItem key={m.slug} value={m.slug}>{m.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!conectada && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1.5" data-testid="responder-no-conectada">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Falta conectar la casilla de {marcaSel?.nombre || "esta marca"} (Configuración → Marcas). Podés escribir la
              respuesta y quedará guardada como borrador en "Respuestas".
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Para</Label>
              <Input value={form.destinatario} onChange={(e) => set({ destinatario: e.target.value })} data-testid="responder-destinatario-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Idioma</Label>
              <Select value={form.idioma} onValueChange={(v) => set({ idioma: v })}>
                <SelectTrigger data-testid="responder-idioma-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Asunto</Label>
            <Input value={form.asunto} onChange={(e) => set({ asunto: e.target.value })} data-testid="responder-asunto-input" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Mensaje</Label>
              <div className="flex items-center gap-1.5">
                {cuerpoAntesTraducir !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={deshacerTraduccion}
                    data-testid="responder-deshacer-traduccion"
                    className="gap-1 h-6 px-1.5 text-[11px] text-slate-500 hover:text-slate-800"
                  >
                    <Undo2 className="h-3 w-3" /> Deshacer
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={traducirAlCliente}
                  disabled={traduciendo}
                  data-testid="responder-traducir-cliente-btn"
                  className="gap-1.5 h-6 px-1.5 text-[11px] text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                >
                  {traduciendo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
                  Traducir al idioma del cliente
                </Button>
              </div>
            </div>
            <Textarea rows={5} value={form.cuerpo} onChange={(e) => set({ cuerpo: e.target.value })}
              placeholder="Escribí la respuesta para el cliente..." data-testid="responder-cuerpo-input" />
          </div>
          {accion.cuerpo && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">El cliente escribió:</Label>
              <pre className="text-[11px] leading-relaxed text-slate-500 whitespace-pre-wrap font-sans bg-slate-50 border border-slate-100 rounded px-3 py-2 max-h-32 overflow-y-auto">
                {accion.cuerpo}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="responder-cancel">Cancelar</Button>
          <Button onClick={enviar} disabled={sending || !conectada} data-testid="responder-enviar-btn" className="gap-2"
            title={!conectada ? `Falta conectar la casilla de ${marcaSel?.nombre || "esta marca"}` : undefined}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
